// src/pages/document/DocumentCategoryListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

/**
 * Document category list page.
 */
export const DocumentCategoryListPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const response = await store.ListCategories();
        setCategories(response?.categories?.categories ?? []);
      } catch {
        // Error is handled by store
      } finally {
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is screen-reader only. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('document.categories')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.categories')}</span>
        </nav>
        <Link to="/documents/categories/new" className="sm:ml-auto">
          <Button>{t('document.createCategory')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('document.categoryFieldName')}</TableHead>
              <TableHead>{t('document.categoryFieldSlug')}</TableHead>
              <TableHead>{t('document.categoryDocumentCount')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableEmpty colSpan={4} message={t('document.noCategories')} />
            ) : (
              categories.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <div style={{ paddingInlineStart: `${Math.min(item.depth ?? 0, 6) * 16}px` }}>
                      <Link to={`/documents/categories/${item.entity?.id || item.id}/edit`} className="font-medium text-primary hover:underline">{item.name || '—'}</Link>
                      {item.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</div>}
                    </div>
                  </TableCell>
                  <TableCell>
                    {String(item.slug ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.document_count ?? 0)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/documents/categories/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(t('document.deleteCategoryConfirm'))) {
                            store.DeleteCategory(item.entity?.id || item.id).then(() => {
                              setCategories((prev) => prev.filter(
                                (c: any) => (c.entity?.id || c.id) !== (item.entity?.id || item.id)
                              ));
                            });
                          }
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentCategoryListPage;
